import { For, Match, Show, Switch, createContext, createEffect, createMemo, createSignal, useContext } from "solid-js";
import { requireViewContext } from "../st-view/st-view";
import { createStore } from "solid-js/store";
import { Color, registerServerMessage } from "common";
import { debounce } from "common";
import { IconsData, LU_PERSON_NOTE_DISPLAYTYPE_Enum, NotificationInfo, PersonNotification, PersonNotificationConfig } from "../../api";
import { personNotifications as api } from 'st-app/api'
import Popover from "../st-popover/st-popover";
import styles from './st-person-notifications.module.css';
import Modal from "../st-modal/st-modal";

const [config, setConfig] = createStore(new PersonNotificationConfig());
api.Config().then(setConfig);

registerServerMessage('RefreshNotificationConfig', data => {
    api.Config().then(setConfig);
});

class StPersonNotificationsContext {
    showModal = createSignal(false)
    notifications = createSignal<PersonNotification[]>([])
}

type NotificationDisplayType = 'banner' | 'icons';

class STPersonNotificationsProps {
    context?: StPersonNotificationsContext
    displayType: NotificationDisplayType = 'icons'
}

const STPersonNotificationsContext = createContext(new StPersonNotificationsContext());
export const useStPersonNotificationsContext = () => useContext(STPersonNotificationsContext);

export default function STPersonNotifications(props: STPersonNotificationsProps) {
    const context = createMemo(() => props.context || new StPersonNotificationsContext());

    return <STPersonNotificationsContext.Provider value={context()}>
        <PersonNotificationsInner displayType={props.displayType} />
        <NotificationModal />
    </STPersonNotificationsContext.Provider>
}

function PersonNotificationsInner(props: { displayType: NotificationDisplayType }) {
    const context = useStPersonNotificationsContext();
    const viewContext = requireViewContext();

    viewContext.events.showModal = () => {
        context.showModal[1](true);
    };

    return <div class={styles.notifications}>
        <Switch>
            <Match when={props.displayType === 'banner'}>
                <Banners />
            </Match>
            <Match when={props.displayType === 'icons'}>
                <Icons />
            </Match>
        </Switch>
    </div>
}

STPersonNotifications.defaultProps = new STPersonNotificationsProps();

function Icons() {
    return <div class={styles.icons}>
    </div>
}

function Banners() {
    const context = useStPersonNotificationsContext();
    const viewCtx = requireViewContext();

    const [notifications, setNotifications] = context.notifications;

    const fetchNotifications = debounce(() => {
        api.FetchNotifications(viewCtx.primaryObjectGU(), viewCtx.identity())
            .then(setNotifications);
    }, 100);

    const notificationDefs = createMemo(() => config.notifications
        .reduce((obj, it) => {
            obj[it.ID ?? ''] = it;
            return obj;
        }, {} as Record<string, NotificationInfo>)
    );

    function isBannerNotification(id: string) {
        const nd = notificationDefs()[id];
        return nd && [LU_PERSON_NOTE_DISPLAYTYPE_Enum.Banner, LU_PERSON_NOTE_DISPLAYTYPE_Enum.IconAndBanner].includes(nd.displayType);
    }

    const bannerNotifications = createMemo(() => {
        return notifications()
            .filter(n => isBannerNotification(n.notCfgGU || ''))
            .reduce((obj, it) => {
                const notCfgGU = it.notCfgGU || '';
                obj[notCfgGU] = obj[notCfgGU] ?? [];
                obj[notCfgGU].push(it);

                return obj;
            }, {} as Record<string, PersonNotification[]>);
    });

    createEffect(() => {
        const id = viewCtx.identity();
        const boid = viewCtx.primaryObjectGU();
        
        if (config.notifications.length > 0 && id && boid) {
            fetchNotifications();
        }
        else {
            fetchNotifications.abort();
            setNotifications([]);
        }
    });

    return <div class="flexbox horizontal" style="flex-wrap: wrap">
        <For each={Object.keys(bannerNotifications())}>{(key: string) =>
            <Banner notCfg={notificationDefs()[key]} perNot={bannerNotifications()[key]} />
        }</For>
    </div>
}

function Banner(props: { notCfg: NotificationInfo, perNot: PersonNotification[] }) {
    const context = useStPersonNotificationsContext();
    const iconDefs = config.icons
        .reduce((obj, it) => {
            obj[it.iconGU ?? ''] = it;
            return obj;
        }, {} as Record<string, IconsData>);

    const icon = createMemo(() => iconDefs[props.notCfg?.iconGU ?? '']);

    const bgColor = new Color(props.notCfg.bannerColor || '#aaa');
    const fgColor = bgColor.gsComplement();
    const borderColor = bgColor.darken(20);

    const count = createMemo(() => props.perNot.length > 1 ? `(${props.perNot.length})` : '');

    return <Popover placement='bottom-start'>
        <Popover.Trigger
            onClick={() => context.showModal[1](true)}
            class={`${styles.banner} flexbox horizontal middle auto`}
            style={{
                color: fgColor.toString(),
                'background-color': bgColor.toString(),
                'border-color': borderColor.toString()
            }} >
            <Show when={icon()}>
                <img alt={props.notCfg.shortDescription} src={icon().iconFilePath} class={styles.icon} />
            </Show>
            <span>{props.notCfg.shortDescription} {count()}</span>
        </Popover.Trigger>
        <Popover.Content>
            <BannerPopover {...props} icon={icon()} />
        </Popover.Content>
    </Popover>
}

function BannerPopover(props: { notCfg: NotificationInfo, perNot: PersonNotification[], icon: IconsData }) {
    return <div class={`${styles.notification} flexbox vertical`}>
        <div class="flexbox horizontal middle">
            <img alt={props.notCfg.shortDescription} src={props.icon.iconFilePath} class={styles.icon} />
            <div>
                <strong innerHTML={props.notCfg.description || props.notCfg.shortDescription}></strong>
            </div>
        </div>
        <Switch>
            <Match when={props.perNot.length === 1}>
                <div innerHTML={props.perNot[0].commentDetail || props.perNot[0].comment}></div>
            </Match>
            <Match when={props.perNot.length > 1}>
                <ol>
                    <For each={props.perNot}>{(perNot)=>
                        <li innerHTML={perNot.commentDetail || perNot.comment}></li>
                    }</For>
                </ol>
            </Match>
        </Switch>
    </div>
}

function NotificationModal() {
    const ctx = useStPersonNotificationsContext();
    const [show, setShow] = ctx.showModal;
    const [notifications, setNotifications] = ctx.notifications;

    return <Modal class={styles.modal} show={[show, setShow]} title="Notifications">
        <div class="flexbox vertical">
            <For each={notifications()}>{not =>
                <NotificationCard perNot={not} />
            }</For>
        </div>
    </Modal>
}

function NotificationCard(props: { perNot: PersonNotification }) {
    const notificationDefs = createMemo(() => config.notifications
        .reduce((obj, it) => {
            obj[it.ID ?? ''] = it;
            return obj;
        }, {} as Record<string, NotificationInfo>)
    );

    const notCfg = createMemo(() => notificationDefs()[props.perNot.notCfgGU ?? '']);

    const iconDefs = config.icons
        .reduce((obj, it) => {
            obj[it.iconGU ?? ''] = it;
            return obj;
        }, {} as Record<string, IconsData>);

    const icon = createMemo(() => iconDefs[notCfg()?.iconGU ?? '']);
    const displayAsBanner = createMemo(() =>
        notCfg()?.displayType == LU_PERSON_NOTE_DISPLAYTYPE_Enum.Banner
        || notCfg()?.displayType == LU_PERSON_NOTE_DISPLAYTYPE_Enum.IconAndBanner
    );

    const bgColor = new Color(notCfg()?.bannerColor || '#ccc');
    const fgColor = bgColor.gsComplement();
    const borderColor = bgColor.darken(20);

    return <div
        class={`${styles.banner} flexbox vertical auto`}
        style={notCfg()?.bannerColor
            ? {
                color: fgColor.toString(),
                'background-color': bgColor.toString(),
                'border-color': borderColor.toString()
            }
            : {}
        }>
        <div class="flexbox horizontal">
            <Show when={icon()}>
                <img alt={notCfg()?.shortDescription} src={icon().iconFilePath} class={styles.icon} />
            </Show>
            <span innerHTML={notCfg()?.description || notCfg()?.shortDescription}></span>
        </div>
        <span innerHTML={props.perNot.commentDetail || props.perNot.comment}></span>
    </div>
}